// Idiomas del juego: español, inglés y portugués (Brasil).
// El idioma se elige con ?lang=xx, se recuerda en localStorage y, si no hay elección, se toma del celular.

import { SHIPS, WEAPONS, PASSIVES, TALENTS, partPerk } from '../sim/index.js';

export const LANGS = { es: 'Español', en: 'English', pt: 'Português' };
const LOCALES = { es: 'es', en: 'en', pt: 'pt-BR' };
const STORE = 'riftfall.lang';

function detect() {
  try {
    const q = new URLSearchParams(location.search).get('lang');
    if (q && LANGS[q]) return q;
  } catch {
    /* sin location */
  }
  try {
    const s = localStorage.getItem(STORE);
    if (s && LANGS[s]) return s;
  } catch {
    /* modo privado */
  }
  const prefs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const l of prefs) {
    const base = String(l ?? '').slice(0, 2).toLowerCase();
    if (LANGS[base]) return base;
  }
  return 'en';
}

export const lang = detect();
export const locale = LOCALES[lang];
if (typeof document !== 'undefined') document.documentElement.lang = lang;

export function setLang(l) {
  if (!LANGS[l] || l === lang) return;
  try {
    localStorage.setItem(STORE, l);
  } catch {
    /* modo privado */
  }
  const url = new URL(location.href);
  url.searchParams.delete('lang');
  location.replace(url.toString());
}

const UI = {
  es: {
    'hud.hull': 'CASCO',
    'hud.kills': 'BAJAS',
    'hud.shards': 'SHARDS',
    'hud.boss': 'GUARDIÁN DEL RIFT',
    'hud.final': 'CORAZÓN DEL RIFT',
    'hud.rift': 'RIFT {n}',
    'hud.combo': 'COMBO',
    'hud.pause': 'Pausa',
    'hud.level': 'NV {n}',

    'menu.shardsTitle': 'Shards (moneda del juego)',
    'menu.coresTitle': 'Núcleos (para mejorar tus habilidades)',
    'menu.riftTitle': 'Saldo de $RIFT',
    'menu.connect': 'Conectar wallet',
    'menu.connecting': 'Conectando…',
    'menu.walletNoNet': 'Wallet (sin red)',
    'menu.sound': 'Sonido',
    'menu.lang': 'Idioma',
    'menu.season': 'TEMPORADA 1 · PROOF OF PLAY',
    'menu.tagline': 'Sobrevive a la grieta. Derrota a los Guardianes. Mejora las habilidades de tu piloto y gana <b>Shards</b>.',
    'menu.selectedShip': 'NAVE SELECCIONADA',
    'menu.change': 'Cambiar',
    'nav.workshop': 'Taller',
    'pt.kicker': 'TU NAVE, A TU MANERA',
    'pt.title': 'Taller',
    'pt.intro': 'Arma tu nave con piezas de cualquier diseño: cambian cómo se ve y dan beneficios. Las piezas repetidas suben de nivel. Salen de las cajas que sueltan los Guardianes y al ganar.',
    'pt.slot.hull': 'Fuselaje',
    'pt.slot.wings': 'Alas',
    'pt.slot.engines': 'Motores',
    'pt.slot.cockpit': 'Cabina',
    'pt.name': '{slot} {ship}',
    'pt.new': 'Nueva pieza: {part}',
    'pt.up': '{part} sube a Nv {n}',
    'pt.maxed': '{part} ya está al máximo',
    'pt.crate': 'Abrir caja',
    'pt.crateCost': 'Caja de piezas · {c} ✦',
    'pt.poor': 'Te faltan Núcleos para la caja.',
    'pt.empty': 'Original',
    'pt.locked': 'Sin descubrir',
    'pt.equip': 'Equipada',
    'pt.bonus': 'Beneficios de tus piezas',
    'pt.none': 'Todavía no tienes piezas equipadas.',
    'pt.note': 'Las piezas no se usan en la Arena ni en el Desafío del Día: ahí todos compiten parejo.',
    'pt.stat.speed': '+{v}% velocidad',
    'pt.stat.maxHp': '+{v}% casco',
    'pt.stat.crit': '+{v}% crítico',
    'pt.stat.area': '+{v}% área',
    'pt.stat.armor': '+{v} blindaje',
    'pt.stat.magnet': '+{v}% imán',
    'pt.stat.might': '+{v}% daño',
    'pt.stat.cooldown': '-{v}% recarga',
    'pt.stat.regen': '+{v} reparación/s',
    'pt.stat.xpGain': '+{v}% experiencia',
    'rv.title': '¿SEGUIR VOLANDO?',
    'rv.text': 'Mira un anuncio y vuelve con la mitad del casco.',
    'rv.watch': 'Ver anuncio y revivir',
    'rv.no': 'No, gracias',
    'rv.double': 'x2 Núcleos',
    'rv.doubled': '¡Núcleos duplicados! +{n} ✦',
    'menu.taglinePortal': 'Sobrevive a la grieta. Derrota a los Guardianes y al <b>Corazón del Rift</b>. Mejora las habilidades de tu piloto.',
    'dc.title': 'DESAFÍO DEL DÍA',
    'wd.title': 'RANKING MUNDIAL DE HOY',
    'wd.none': 'Todavía nadie entró al ranking de hoy: ¡sé el primero!',
    'wd.you': 'Tu puesto: <b>#{n}</b>',
    'wd.out': 'Para entrar al top {max} hacen falta <b>{score}</b> puntos.',
    'wd.as': '✎ {name}',
    'wd.prompt': 'Tu nombre de piloto para el ranking (máximo 16 letras):',
    'wd.checking': '🌍 Ranking mundial: verificando la partida…',
    'wd.rank': '🌍 ¡Puesto #{n} del mundo hoy!',
    'wd.kept': '🌍 Tu mejor marca de hoy sigue en el puesto #{n}',
    'wd.outRow': '🌍 Fuera del top {max} (el último tiene {score})',
    'wd.err': '🌍 No se pudo enviar al ranking mundial',
    'rk.title': 'RANKING DE HOY',
    'rk.more': 'VER RANKING COMPLETO',
    'rk.none': 'Todavía nadie jugó hoy: ¡tu partida puede ser la primera!',
    'rk.play': 'Jugá una partida y tu mejor puntaje de hoy aparece acá para todos.',
    'rk.youAre': 'Estás en el puesto #{n}.',
    'rk.intro': 'Todas las partidas de todos los jugadores, verificadas por la web del juego. Cada jugador aparece con su mejor puntaje.',
    'rk.checking': '🏆 Ranking: verificando la partida…',
    'rk.rank': '🏆 ¡Puesto #{n} del ranking de hoy!',
    'rk.out': '🏆 Fuera del top {max} de hoy',
    'rk.allRank': '#{n} histórico',
    'rk.err': '🏆 No se pudo cargar el ranking. Probá de nuevo en un rato.',
    'rk.sendErr': '🏆 No se pudo enviar la partida al ranking',
    'dc.rules': 'Todos juegan la misma partida: nave SPARK, sin habilidades, Rift 1.',
    'dc.best': 'Tu mejor de hoy: {score} pts · {time}',
    'dc.tries': '{n} intentos',
    'dc.next': 'Nuevo desafío en {h} h {m} min',
    'dc.play': 'JUGAR DESAFÍO',
    'dc.again': 'Mejorar marca',
    'dc.hud': 'DESAFÍO #{n}',
    'dc.newBest': '¡Tu mejor marca del desafío #{n}!',
    'dc.row': 'Desafío #{n} · tu mejor de hoy',
    'ann.daily': 'DESAFÍO #{n}',
    'ann.dailySub': 'la misma partida para todo el mundo',
    'r.challenge': 'DESAFÍO',
    'sh.daily': 'RIFTFALL · Desafío #{n} ⏱ {time} · 💥 {kills} · ★ {score} ¿Me superas?',
    'du.title': 'DUELO CON AMIGOS',
    'du.text': 'Juega una partida y envía el link: tu amigo juega el mismo mapa con la misma nave. Gana quien haga más puntos.',
    'du.play': 'JUGAR Y RETAR',
    'du.from': '⚔ {name} te retó a un duelo',
    'du.fromAnon': '⚔ Un amigo te retó a un duelo',
    'du.mark': '{score} pts · {time} · {kills} bajas',
    'du.sub': 'Mismo mapa, misma nave. ¿Le ganas?',
    'du.accept': 'ACEPTAR DUELO',
    'du.hud': 'DUELO',
    'du.vs': 'contra {name} · {score} pts',
    'du.new': 'haz tu marca y después reta a un amigo',
    'du.friend': 'tu amigo',
    'du.won': '¡Le ganaste a {name} por {diff} puntos!',
    'du.lost': '{name} te ganó por {diff} puntos',
    'du.tie': '¡Empate con {name}!',
    'du.ready': 'Envía el link y tu amigo jugará este mismo mapa.',
    'du.name': 'Tu nombre (opcional)',
    'du.send': '⚔ RETAR A UN AMIGO',
    'du.sendBack': '⚔ DEVOLVER EL RETO',
    'du.again': 'MISMO MAPA',
    'du.bad': 'El link del duelo está incompleto o fue modificado.',
    'sh.duel': '⚔ Te reto a un duelo en RIFTFALL: hice {score} puntos y aguanté {time}. Mismo mapa, misma nave. ¿Me ganas?',
    'pwa.install': '⬇ Instalar',
    'pwa.ios': 'En iPhone: toca Compartir y después «Agregar a inicio».',
    'f.kicker': 'APOYA EL JUEGO',
    'f.title': 'Pase Fundador',
    'f.banner': '★ PASE FUNDADOR',
    'up.text': '¿Te gusta RIFTFALL? Hazte Fundador desde US$3: estela dorada, pinturas exclusivas y la LEVIATHAN en el lanzamiento.',
    'up.cta': '★ VER PASE FUNDADOR',
    'up.no': 'Ahora no',
    'f.bannerSub': 'Apoya RIFTFALL antes del lanzamiento · desde US$3',
    'f.badge': 'FUNDADOR',
    'f.intro': 'RIFTFALL lo hace una sola persona, sin inversores. Con el Pase Fundador ayudas a lanzarlo en la red principal y te llevas objetos exclusivos para siempre.',
    'f.tier.pilot': 'Piloto Fundador',
    'f.tier.gold': 'Fundador Oro',
    'f.tier.legend': 'Fundador Leyenda',
    'f.perk.badge': 'Insignia ★ Fundador',
    'f.perk.trail': 'Estela dorada para tu nave',
    'f.perk.prev': 'Todo lo del nivel anterior',
    'f.perk.skinGold': 'Pintura dorada para todas las naves',
    'f.perk.skinPrisma': 'Pintura «Prisma» exclusiva',
    'f.perk.leviathan': 'Nave LEVIATHAN (NFT) en tu wallet cuando el juego salga a la red principal',
    'f.payUsdt': 'Pagar {n} USDT',
    'f.payBnb': 'Pagar ≈{n} BNB',
    'f.owned': '✓ Ya es tuyo',
    'f.youAre': 'Eres {tier}',
    'f.viewTx': 'Ver el pago en BscScan',
    'f.owner': 'Dueño del juego · todo desbloqueado',
    'f.viewWallet': 'Ver la wallet del creador en BscScan',
    'f.skin': 'Pintura',
    'f.skin.original': 'Original',
    'f.skin.founder': 'Dorada',
    'f.skin.prisma': 'Prisma',
    'f.restoreTitle': '¿Ya pagaste desde otro dispositivo?',
    'f.restoreHint': 'Pega el hash de la transacción (empieza con 0x) y toca Verificar.',
    'f.verify': 'Verificar pago',
    'f.verified': '¡Gracias! Ya eres {tier}',
    'f.howTo': 'Se paga en BNB Smart Chain (red principal) desde tu wallet y va directo a la wallet del creador ({addr}). Necesitas un poquito de BNB para la comisión: menos de 1 centavo.',
    'f.legal': 'Es una compra de objetos del juego para apoyar su desarrollo. No es una inversión: no da derecho a tokens ni a ganancias.',
    'f.stage.wallet': 'Confirma en tu wallet…',
    'f.stage.confirm': 'Esperando la red…',
    'f.badHash': 'Ese no es un hash de transacción válido.',
    'f.notFound': 'No encontramos esa transacción en BNB Chain.',
    'f.err.pending': 'La transacción todavía no se confirmó. Prueba en un minuto.',
    'f.err.failed': 'Esa transacción falló en la red.',
    'f.err.notToTreasury': 'Esa transacción no es un pago a RIFTFALL.',
    'f.err.tooLow': 'El monto no alcanza para ningún nivel.',
    'f.err.otherPayer': 'Ese pago lo hizo otra wallet.',
    'f.err.offline': 'Sin conexión con el servidor. Conservá el comprobante si ya pagaste.',
    'f.err.priceUnavailable': 'No se pudo fijar el importe. Probá de nuevo antes de pagar.',
    'f.err.signature': 'No se pudo verificar tu firma. Volvé a conectar la wallet.',
    'f.err.session': 'Volvé a entrar a tu Cuenta Rift antes de continuar.',
    'f.err.otherItem': 'Ese pago corresponde a otro artículo.',
    'f.err.tooManyOrders': 'Llegaste al límite de pedidos de hoy. Probá mañana.',
    'f.err.orderExpired': 'El pedido venció antes de confirmarse el pago. Contactá al dueño con el comprobante.',
    'f.err.badOrder': 'El pago no coincide con el pedido. Conservá el comprobante.',
    'f.err.manualReview': 'El pago anterior necesita revisión del dueño. Conservá el comprobante.',
    'f.err.accountChanged': 'Entraste a tu otra cuenta. Volvé a iniciar la compra cuando termine de cargar.',
    'f.err.wrongChain': 'No se pudo comprobar el pago en BNB Chain. No vuelvas a pagar.',
    'f.err.claimed': 'Ese comprobante ya fue reconocido en una cuenta.',
    'f.err.otherItem': 'Ese pago es de otra compra (un estético de Rift Cargo), no del Pase Fundador.',
    'acct.guest': 'Invitado',
    'acct.title': 'Cuenta',
    'f.priceErr': 'Precio del BNB no disponible',
    'menu.riftLevel': 'NIVEL DEL RIFT',
    'menu.riftPrev': 'Nivel anterior',
    'menu.riftNext': 'Nivel siguiente',
    'rift.normal': 'El Rift tal como es',
    'rift.mods': 'Enemigos +{hp}% vida · +{dmg}% daño',
    'rift.reward': 'Premio x{r}',
    'rift.locked': 'Gana el nivel {n} para desbloquear el {m}',
    'menu.play': 'JUGAR',
    'menu.arena': 'ARENA',
    'menu.hintKeys': 'WASD, flechas o el mouse para moverte · las armas disparan solas · 1-2-3 para elegir mejoras',
    'menu.hintTouch': 'Arrastra el dedo en cualquier parte para moverte · las armas disparan solas',
    'menu.daily': 'Misiones diarias',
    'menu.streak': 'Racha {n}🔥',
    'menu.footer': 'Cada partida se re-simula antes de pagar recompensas.',
    'menu.cargo': 'Nuevo: Rift Cargo, tu empresa de transporte espacial →',
    'nav.hangar': 'Hangar',
    'nav.market': 'Mercado',
    'nav.ranking': 'Ranking',
    'nav.vault': 'Canjear',
    'nav.economy': 'Economía',
    'nav.talents': 'Habilidades',
    'net.connecting': 'Conectando…',
    'net.online': '● En línea · red {net} ({id})',
    'net.onlineNoChain': '● En línea · blockchain no configurada (modo invitado)',
    'net.shop': '● Tienda de naves activa · partidas en modo práctica',
    'net.shopTest': '● Red de pruebas · tokens y naves sin valor real',
    'net.practice': '● Modo práctica · tu progreso se guarda en este dispositivo',

    'sheet.close': 'Cerrar',
    'sheet.loading': 'Cargando…',

    'lvl.kicker': 'SUBISTE DE NIVEL',
    'lvl.title': 'NIVEL {n}',
    'lvl.chestKicker': 'BOTÍN DE JEFE',
    'lvl.chestTitle': 'COFRE DEL GUARDIÁN',
    'lvl.hint': 'Elige con clic, toque o las teclas 1 · 2 · 3',
    'lvl.new': 'NUEVO',
    'lvl.evo': 'EVOLUCIÓN',
    'choice.evoText': '{desc}. Evoluciona tu {weapon}.',
    'choice.repair': 'Kit de reparación',
    'choice.repairText': 'Recupera 40% de la vida',
    'choice.cache': 'Alijo de Shards',
    'choice.cacheText': '+2 Shards',

    'pause.title': 'PAUSA',
    'pause.resume': 'Continuar',
    'pause.quit': 'Terminar partida',

    'go.kicker': 'FIN DE LA PARTIDA',
    'go.victory': 'VICTORIA',
    'go.titleVictory': 'RIFT SELLADO',
    'go.titleRetreat': 'RETIRADA',
    'go.titleDead': 'NAVE DESTRUIDA',
    'go.time': 'TIEMPO',
    'go.kills': 'BAJAS',
    'go.combo': 'COMBO MÁX',
    'go.score': 'PUNTAJE',
    'go.verifying': 'Verificando partida en el servidor (re-simulación completa)…',
    'go.verified': '✔ Partida verificada: el servidor re-simuló cada tick y obtuvo el mismo resultado.',
    'go.practice': 'Modo práctica: tu progreso se guarda en este dispositivo.',
    'go.shardsEarned': 'SHARDS GANADOS',
    'go.coresEarned': 'NÚCLEOS GANADOS',
    'go.again': 'JUGAR OTRA VEZ',
    'go.menu': 'Menú',
    'go.share': 'Compartir',
    'go.talents': 'Habilidades',
    'go.rowRun': 'Botín de la partida (x{m} por tu nave)',
    'go.rowStreak': 'Bonus de racha diaria',
    'go.rowMission': 'Misión: {name}',
    'go.rowArena': 'Puntaje registrado en la Arena',
    'go.rowCores': 'Núcleos de la partida',
    'go.newBest': '¡NUEVO RÉCORD!',
    'go.riftUnlocked': 'Nivel del Rift {n} desbloqueado',
    'go.titleCollapse': 'EL RIFT COLAPSÓ',
    'go.canUpgrade': 'Tienes Núcleos para mejorar una habilidad',

    'ann.survive': 'SOBREVIVE',
    'ann.surviveSub': 'el Rift se está abriendo',
    'ann.arena': 'ARENA',
    'ann.arenaSub': 'el mejor puntaje se lleva el bote',
    'ann.boss': '⚠ GUARDIÁN DEL RIFT',
    'ann.bossSub': 'se aproxima',
    'ann.swarm': 'ENJAMBRE',
    'ann.swarmSub': 'te están rodeando',
    'ann.bossDead': 'GUARDIÁN DERROTADO',
    'ann.bossDeadSub': 'recoge su cofre',
    'ann.evolved': 'arma evolucionada',
    'ann.final': '⚠ EL CORAZÓN DEL RIFT DESPIERTA',
    'ann.finalSub': 'destrúyelo para ganar',
    'ann.meteors': 'LLUVIA DE METEORITOS',
    'ann.meteorsSub': 'esquiva los círculos rojos · también dañan a los enemigos',
    'ann.elites': 'ESCUADRÓN DE ÉLITE',
    'ann.elitesSub': 'tres enemigos de élite en camino',

    'tut.moveTouch': '<b>Arrastra el dedo</b> en cualquier parte para moverte.',
    'tut.moveKeys': 'Muévete con <b>WASD</b>, las <b>flechas</b> o el <b>mouse</b>.',
    'tut.auto': 'Tus armas <b>disparan solas</b>. Tú solo esquiva y sobrevive.',
    'tut.gems': 'Recoge los <b>cristales</b> para subir de nivel y elegir mejoras.',
    'tut.evo': 'Arma al <b>nivel 5</b> + su mejora pareja = <b>EVOLUCIÓN</b>. Los jefes sueltan cofres.',
    'tut.final': 'A los <b>10:00</b> despierta el <b>Corazón del Rift</b>: destrúyelo para ganar.',

    'toast.practice': 'Modo práctica: ganas Núcleos para tus habilidades. Los Shards llegan con el servidor de recompensas.',
    'toast.walletConnected': 'Wallet conectada: {a}',
    'toast.noChain': 'La blockchain no está configurada en este servidor. Puedes jugar como invitado.',
    'toast.transferred': 'Listo: tu progreso llegó a este navegador.',
    'bridge.got': 'Ruta Rift: +{n} Núcleos por tu empresa en Rift Cargo',
    'bridge.next': 'nivel {lv} allá = +{n} Núcleos acá',
    'mv.title': 'Tu progreso en otro dispositivo',
    'mv.text': 'Abrí este link en el otro celular o compu y vas a tener el mismo progreso y el mismo nombre en el ranking. Es tuyo: no lo compartas con otros jugadores.',
    'mv.btn': 'Copiar link de mi progreso',
    'mv.copied': 'Link copiado. Pegalo en el otro dispositivo.',

    'fx.heal': '+VIDA',
    'fx.combo': 'COMBO x{n}!',

    'err.unknown': 'Error desconocido',
    'err.rejected': 'Operación cancelada en la wallet',
    'err.pending': 'MetaMask ya tiene una ventana abierta esperando tu respuesta: ábrela, acéptala o recházala y vuelve a intentar.',
    'err.funds': 'Fondos insuficientes para pagar la operación y el gas',
    'err.network': 'La red rechazó la operación. Revisa tu saldo y que la wallet esté en la red del juego.',
    'err.connectFirst': 'Conecta tu wallet primero',
    'err.noWallet': 'No se detectó ninguna wallet. Instala MetaMask, Rabby o Coinbase Wallet.',

    'ship.yield': 'Recompensa x{m}',

    'tal.kicker': 'PILOTO',
    'tal.title': 'Habilidades',
    'tal.intro': 'Mejoras permanentes que se aplican en cada partida (menos en la Arena, donde todos compiten igual). Se compran con Núcleos ✦, que ganas jugando: bajas, tiempo, Guardianes, Shards recogidos y misiones.',
    'tal.local': 'Tu progreso se guarda en este dispositivo.',
    'tal.cores': 'TUS NÚCLEOS',
    'tal.perLevel': '{desc} por nivel',
    'tal.upgrade': 'Mejorar · {c} ✦',
    'tal.max': 'MÁXIMO',
    'tal.done': '{name} subió a nivel {n}',
    'tal.poor': 'Te faltan Núcleos: juega más partidas',
    'tal.note': 'Los Núcleos no se canjean por tokens: son solo para hacer más fuerte a tu piloto.',
    'tal.stats': 'Tus estadísticas',

    'st.runs': 'PARTIDAS',
    'st.best': 'MEJOR PUNTAJE',
    'st.bestTime': 'MEJOR TIEMPO',
    'st.kills': 'BAJAS',
    'st.bosses': 'GUARDIANES',
    'st.victories': 'VICTORIAS',
    'st.lifetimeShards': 'SHARDS TOTALES',
    'st.streak': 'RACHA',
    'st.days': '{n} días',
    'st.day': '1 día',

    'h.kicker': 'NAVES',
    'h.title': 'Hangar',
    'h.intro': 'La nave define tu arma inicial, tus estadísticas y el multiplicador de Shards. Cada nivel de Forja suma +2% de daño y +3% de recompensa.',
    'h.yours': 'Tus naves',
    'h.connectMine': 'Conecta tu wallet para ver y usar tus naves NFT.',
    'ship.needWallet': 'Conecta tu wallet para volar con esta nave.',
    'toast.sparkFallback': 'Sin la wallet conectada no puedes usar la {name}: esta partida va con la SPARK.',
    'h.free': 'GRATIS',
    'h.inUse': 'En uso',
    'h.use': 'Usar',
    'h.demo': 'DEMO',
    'h.try': 'Probar',
    'h.forge': 'Forjar NV {n} · {c} {sym}',
    'h.forging': 'Forjando…',
    'h.forged': '¡Nave #{id} subió a nivel {n}!',
    'h.sell': 'Vender',
    'h.sellPrompt': 'Precio de venta en {sym}:',
    'h.publishing': 'Publicando…',
    'h.published': 'Nave publicada en el Mercado',
    'h.shipyard': 'Astillero · naves NFT',
    'h.connect': 'Conecta tu wallet para comprar naves NFT. Son tuyas: puedes usarlas, forjarlas o venderlas en el Mercado.',
    'h.buying': 'Comprando…',
    'h.bought': '¡{name} es tuya!',
    'h.left': '{n} de {m} disponibles',
    'h.soldOut': 'AGOTADA',
    'h.noFunds': 'No te alcanza: tienes {have} {sym} y esta nave cuesta {price} {sym}.',
    'h.payRift': 'Puedes pagarla con RIFT (botón dorado).',
    'h.faucet': 'Los tBNB de prueba se piden gratis en un grifo (faucet).',
    'o.title': 'Panel del dueño (solo lo ves tú)',
    'o.intro': 'Cambia el precio de cada nave. En RIFT, 0 significa que esa nave no se vende con RIFT. Cada cambio es una transacción que firmas en tu wallet (cuesta centavos de {sym}).',
    'o.native': 'Precio en {sym}',
    'o.rift': 'Precio en RIFT',
    'o.save': 'Guardar',
    'o.saving': 'Confirmando…',
    'o.saved': 'Precio de {name} actualizado',
    'o.withdraw': 'Cobrar {v} {sym} de ventas',
    'o.withdrawn': 'Ventas cobradas a tu wallet',
    'o.invalid': 'Precio inválido: usa solo números (por ejemplo 50000 o 0.015)',
    'h.split': 'Los pagos en RIFT se reparten: 40% se quema, 30% vuelve al pool de recompensas de los jugadores y 30% a la tesorería del proyecto.',
    'h.ready': '{name} lista para despegar',
    'h.noShop': 'La tienda de naves NFT abre con el lanzamiento del token. Mientras tanto, la SPARK gratuita puede ganar la partida.',
    'h.testnet': 'Red de pruebas: los tBNB y los tokens de esta versión no tienen valor real. Sirven para probar la tienda, la forja y el mercado antes del lanzamiento.',

    'n.noChain': 'Las naves NFT, el mercado y el canje abren con el lanzamiento del token. Mientras tanto puedes jugar gratis con la SPARK y mejorar tus habilidades.',

    'm.kicker': 'P2P',
    'm.title': 'Mercado',
    'm.connect': 'Conecta tu wallet para comprar y vender naves con otros pilotos.',
    'm.intro': 'Compra naves de otros jugadores pagando en RIFT. Comisión del mercado: {fee}% (la paga el vendedor). Sin custodia: la nave queda en la wallet del vendedor hasta la venta.',
    'm.empty': 'No hay naves en venta ahora mismo. Publica la tuya desde el Hangar.',
    'm.withdraw': 'Retirar',
    'm.buy': 'Comprar · {p} {sym}',
    'm.withdrawing': 'Retirando…',
    'm.withdrawn': 'Anuncio retirado',
    'm.bought': '¡Compraste la nave #{id}!',
    'm.seller': 'Vende {a}',

    'a.kicker': 'TORNEOS',
    'a.title': 'Arena',
    'a.server': 'Los torneos de la Arena se activan cuando el servidor de recompensas esté en línea.',
    'a.intro': 'Paga la inscripción en RIFT y compite por el bote. Todos usan la nave SPARK y sin habilidades del piloto (solo cuenta cómo juegas). Cuenta tu mejor puntaje; puedes jugar todas las veces que quieras hasta el cierre.',
    'a.none': 'No hay un torneo abierto en este momento. ¡Vuelve pronto!',
    'a.pool': 'BOTE',
    'a.fee': 'INSCRIPCIÓN',
    'a.pilots': 'PILOTOS',
    'a.closes': 'CIERRA EN',
    'a.split': 'Del bote: {rake}% para el proyecto, {burn}% se quema y el resto se reparte entre el top {top} (1º: {first}%). Lo no repartido vuelve al pool de recompensas.',
    'a.connect': 'Conecta tu wallet para inscribirte en la Arena.',
    'a.play': 'JUGAR ARENA',
    'a.enter': 'Inscribirme · {fee} {sym}',
    'a.entering': 'Inscribiendo…',
    'a.entered': '¡Inscrito! Ya puedes jugar la Arena',
    'a.standings': 'Clasificación',
    'a.noScores': 'Aún no hay puntajes en este torneo. ¡El primero marca el ritmo!',
    'a.past': 'Torneos anteriores',
    'a.pastRow': '#{id} · ganador: {name} ({score} pts)',

    'r.kicker': 'TOP PILOTOS',
    'r.title': 'Ranking',
    'r.server': 'El ranking se activa cuando el servidor de recompensas esté en línea.',
    'r.empty': 'Todavía no hay partidas. ¡Sé el primero!',
    'r.today': 'HOY',
    'r.all': 'HISTÓRICO',
    'r.pilot': 'Piloto',
    'r.ship': 'Nave',
    'r.time': 'Tiempo',
    'r.score': 'Puntaje',

    'v.kicker': 'SHARDS → $RIFT',
    'v.title': 'Canjear',
    'v.server': 'El canje de Shards por el token se activa con el lanzamiento y el servidor de recompensas. Mientras tanto, juega para mejorar tus habilidades.',
    'v.yours': 'TUS SHARDS',
    'v.rate': 'TASA',
    'v.min': 'MÍNIMO',
    'v.balance': 'SALDO RIFT',
    'v.connect': 'Conecta tu wallet para convertir tus Shards en tokens $RIFT reales.',
    'v.today': 'Emisión de hoy: quedan {left} de {budget} RIFT. Límite por jugador: {cap} RIFT/día.',
    'v.claim': 'Canjear',
    'v.signing': 'Firmando…',
    'v.signed': 'Vale firmado por el servidor. Confirma la transacción en tu wallet.',
    'v.received': '¡$RIFT recibidos en tu wallet!',
    'v.voucher': 'El servidor firma un vale (EIP-712) válido 1 hora. Si no lo cobras, vence y los Shards vuelven a tu cuenta automáticamente.',
    'v.history': 'Historial',
    'v.pending': 'PENDIENTE',
    'v.paid': 'COBRADO',
    'v.expired': 'VENCIDO',
    'v.collect': 'Cobrar',
    'v.collecting': 'Cobrando…',
    'v.receivedShort': '¡$RIFT recibidos!',

    'e.kicker': 'TRANSPARENCIA',
    'e.title': 'Economía $RIFT',
    'e.supply': '$RIFT tiene un suministro fijo de 1.000.000.000. El contrato no permite crear más tokens, no cobra impuestos por transferencia y nadie puede congelar tu saldo.',
    'e.alloc': 'Distribución inicial',
    'e.a1': 'Recompensas de jugadores (vault con halving)',
    'e.a2': 'Tesorería (marketing, botes, alianzas)',
    'e.a3': 'Liquidez en DEX',
    'e.a4': 'Equipo (bloqueado: 6 meses + 24 lineal)',
    'e.a5': 'Comunidad y airdrops',
    'e.emission': 'Emisión para jugadores',
    'e.budget': 'PRESUPUESTO HOY',
    'e.remaining': 'RESTANTE HOY',
    'e.vault': 'EN EL VAULT',
    'e.cap': 'TOPE/JUGADOR',
    'e.halving': 'La emisión diaria se reduce a la mitad cada 180 días. Parte de lo que se gasta en Forja, compras con RIFT y torneos vuelve al vault, alargando su vida.',
    'e.where': 'A dónde va cada RIFT gastado',
    'e.action': 'Acción',
    'e.burn': 'Quema',
    'e.pool': 'Pool jugadores',
    'e.project': 'Proyecto',
    'e.rowForge': 'Forja / compra de naves en RIFT',
    'e.rowArena': 'Inscripción de Arena',
    'e.rowArenaPool': 'premios no asignados',
    'e.rowMarket': 'Venta en el Mercado',
    'e.rowMarketFee': '5% comisión',
    'e.activity': 'Actividad',
    'e.players': 'PILOTOS',
    'e.verified': 'PARTIDAS VERIFICADAS',
    'e.rejected': 'RECHAZADAS',
    'e.issued': 'SHARDS EMITIDOS',
    'e.contracts': 'Contratos',
    'e.watch': 'Ver {sym} en mi wallet',

    'p.save': 'Guardar',
    'p.saved': 'Nombre actualizado',
    'p.wallet': 'Wallet vinculada: {a}',
    'p.guest': 'Juegas como invitado. Conecta una wallet para guardar tu progreso en ella y canjear Shards.',
    'p.connect': 'Conectar wallet',
    'p.noServer': 'Sin conexión con el servidor.',

    'sh.text': 'Sobreviví {time} en RIFTFALL: {kills} bajas y {score} puntos 🚀 ¿Me superas?',
    'sh.textWin': '¡Sellé el Rift en RIFTFALL! {kills} bajas y {score} puntos 🏆 ¿Me superas?',
    'sh.cta': 'Juega gratis en el navegador',
    'sh.copied': 'Texto copiado: pégalo en WhatsApp o donde quieras',
    'sh.ship': 'NAVE'
  },

  en: {
    'hud.hull': 'HULL',
    'hud.kills': 'KILLS',
    'hud.shards': 'SHARDS',
    'hud.boss': 'RIFT GUARDIAN',
    'hud.final': 'RIFT HEART',
    'hud.rift': 'RIFT {n}',
    'hud.combo': 'COMBO',
    'hud.pause': 'Pause',
    'hud.level': 'LV {n}',

    'menu.shardsTitle': 'Shards (game currency)',
    'menu.coresTitle': 'Cores (to upgrade your skills)',
    'menu.riftTitle': '$RIFT balance',
    'menu.connect': 'Connect wallet',
    'menu.connecting': 'Connecting…',
    'menu.walletNoNet': 'Wallet (no network)',
    'menu.sound': 'Sound',
    'menu.lang': 'Language',
    'menu.season': 'SEASON 1 · PROOF OF PLAY',
    'menu.tagline': 'Survive the rift. Defeat the Guardians. Upgrade your pilot’s skills and earn <b>Shards</b>.',
    'menu.selectedShip': 'SELECTED SHIP',
    'menu.change': 'Change',
    'nav.workshop': 'Workshop',
    'pt.kicker': 'YOUR SHIP, YOUR WAY',
    'pt.title': 'Workshop',
    'pt.intro': 'Build your ship with parts from any design: they change its look and give bonuses. Duplicate parts level up. They come from crates dropped by Guardians and from victories.',
    'pt.slot.hull': 'Hull',
    'pt.slot.wings': 'Wings',
    'pt.slot.engines': 'Engines',
    'pt.slot.cockpit': 'Cockpit',
    'pt.name': '{ship} {slot}',
    'pt.new': 'New part: {part}',
    'pt.up': '{part} levels up to {n}',
    'pt.maxed': '{part} is already maxed',
    'pt.crate': 'Open crate',
    'pt.crateCost': 'Parts crate · {c} ✦',
    'pt.poor': 'Not enough Cores for a crate.',
    'pt.empty': 'Original',
    'pt.locked': 'Undiscovered',
    'pt.equip': 'Equipped',
    'pt.bonus': 'Bonuses from your parts',
    'pt.none': 'No parts equipped yet.',
    'pt.note': 'Parts are not used in the Arena or the Daily Challenge: everyone competes on equal terms there.',
    'pt.stat.speed': '+{v}% speed',
    'pt.stat.maxHp': '+{v}% hull',
    'pt.stat.crit': '+{v}% crit',
    'pt.stat.area': '+{v}% area',
    'pt.stat.armor': '+{v} armor',
    'pt.stat.magnet': '+{v}% magnet',
    'pt.stat.might': '+{v}% damage',
    'pt.stat.cooldown': '-{v}% cooldown',
    'pt.stat.regen': '+{v} repair/s',
    'pt.stat.xpGain': '+{v}% experience',
    'rv.title': 'KEEP FLYING?',
    'rv.text': 'Watch an ad and come back with half your hull.',
    'rv.watch': 'Watch ad & revive',
    'rv.no': 'No, thanks',
    'rv.double': 'x2 Cores',
    'rv.doubled': 'Cores doubled! +{n} ✦',
    'menu.taglinePortal': 'Survive the Rift. Defeat the Guardians and the <b>Rift Heart</b>. Upgrade your pilot\'s skills.',
    'dc.title': 'DAILY CHALLENGE',
    'wd.title': "TODAY'S WORLD RANKING",
    'wd.none': 'Nobody is on today\'s ranking yet: be the first!',
    'wd.you': 'Your place: <b>#{n}</b>',
    'wd.out': 'You need <b>{score}</b> points to make the top {max}.',
    'wd.as': '✎ {name}',
    'wd.prompt': 'Your pilot name for the ranking (16 letters max):',
    'wd.checking': '🌍 World ranking: verifying your run…',
    'wd.rank': '🌍 #{n} in the world today!',
    'wd.kept': '🌍 Your best today is still #{n}',
    'wd.outRow': '🌍 Outside the top {max} (last place has {score})',
    'wd.err': '🌍 Could not reach the world ranking',
    'rk.title': "TODAY'S RANKING",
    'rk.more': 'SEE FULL RANKING',
    'rk.none': 'Nobody has played today yet: your run can be the first!',
    'rk.play': 'Play a run and your best score of the day shows up here for everyone.',
    'rk.youAre': 'You are #{n}.',
    'rk.intro': 'Every run from every player, verified by the game website. Each player appears with their best score.',
    'rk.checking': '🏆 Ranking: verifying your run…',
    'rk.rank': "🏆 #{n} on today's ranking!",
    'rk.out': '🏆 Outside today\'s top {max}',
    'rk.allRank': '#{n} all time',
    'rk.err': '🏆 Could not load the ranking. Try again in a while.',
    'rk.sendErr': '🏆 Could not send your run to the ranking',
    'dc.rules': 'Everyone plays the same run: SPARK ship, no skills, Rift 1.',
    'dc.best': 'Your best today: {score} pts · {time}',
    'dc.tries': '{n} tries',
    'dc.next': 'New challenge in {h} h {m} min',
    'dc.play': 'PLAY CHALLENGE',
    'dc.again': 'Beat your score',
    'dc.hud': 'CHALLENGE #{n}',
    'dc.newBest': 'Your best in challenge #{n}!',
    'dc.row': 'Challenge #{n} · your best today',
    'ann.daily': 'CHALLENGE #{n}',
    'ann.dailySub': 'the same run for the whole world',
    'r.challenge': 'CHALLENGE',
    'sh.daily': 'RIFTFALL · Challenge #{n} ⏱ {time} · 💥 {kills} · ★ {score} Can you beat me?',
    'du.title': 'DUEL A FRIEND',
    'du.text': 'Play a run and send the link: your friend plays the same map with the same ship. Highest score wins.',
    'du.play': 'PLAY & CHALLENGE',
    'du.from': '⚔ {name} challenged you to a duel',
    'du.fromAnon': '⚔ A friend challenged you to a duel',
    'du.mark': '{score} pts · {time} · {kills} kills',
    'du.sub': 'Same map, same ship. Can you win?',
    'du.accept': 'ACCEPT DUEL',
    'du.hud': 'DUEL',
    'du.vs': 'vs {name} · {score} pts',
    'du.new': 'set a score, then challenge a friend',
    'du.friend': 'your friend',
    'du.won': 'You beat {name} by {diff} points!',
    'du.lost': '{name} beat you by {diff} points',
    'du.tie': 'Tie with {name}!',
    'du.ready': 'Send the link and your friend will play this same map.',
    'du.name': 'Your name (optional)',
    'du.send': '⚔ CHALLENGE A FRIEND',
    'du.sendBack': '⚔ SEND IT BACK',
    'du.again': 'SAME MAP',
    'du.bad': 'The duel link is incomplete or was modified.',
    'sh.duel': '⚔ I challenge you to a RIFTFALL duel: I scored {score} and lasted {time}. Same map, same ship. Can you beat me?',
    'pwa.install': '⬇ Install',
    'pwa.ios': 'On iPhone: tap Share, then «Add to Home Screen».',
    'f.kicker': 'SUPPORT THE GAME',
    'f.title': 'Founder Pass',
    'f.banner': '★ FOUNDER PASS',
    'up.text': 'Enjoying RIFTFALL? Become a Founder from US$3: golden trail, exclusive paints and the LEVIATHAN at launch.',
    'up.cta': '★ SEE FOUNDER PASS',
    'up.no': 'Not now',
    'f.bannerSub': 'Back RIFTFALL before launch · from US$3',
    'f.badge': 'FOUNDER',
    'f.intro': 'RIFTFALL is made by one person, with no investors. The Founder Pass helps launch it on mainnet and gives you exclusive items forever.',
    'f.tier.pilot': 'Founder Pilot',
    'f.tier.gold': 'Gold Founder',
    'f.tier.legend': 'Legend Founder',
    'f.perk.badge': '★ Founder badge',
    'f.perk.trail': 'Golden engine trail',
    'f.perk.prev': 'Everything in the previous tier',
    'f.perk.skinGold': 'Gold paint for every ship',
    'f.perk.skinPrisma': 'Exclusive «Prisma» paint',
    'f.perk.leviathan': 'LEVIATHAN ship (NFT) in your wallet when the game goes to mainnet',
    'f.payUsdt': 'Pay {n} USDT',
    'f.payBnb': 'Pay ≈{n} BNB',
    'f.owned': '✓ You own it',
    'f.youAre': 'You are a {tier}',
    'f.viewTx': 'View the payment on BscScan',
    'f.owner': 'Game owner · everything unlocked',
    'f.viewWallet': "View the creator's wallet on BscScan",
    'f.skin': 'Paint',
    'f.skin.original': 'Original',
    'f.skin.founder': 'Gold',
    'f.skin.prisma': 'Prisma',
    'f.restoreTitle': 'Already paid on another device?',
    'f.restoreHint': 'Paste the transaction hash (starts with 0x) and tap Verify.',
    'f.verify': 'Verify payment',
    'f.verified': 'Thank you! You are now a {tier}',
    'f.howTo': "Paid on BNB Smart Chain (mainnet) from your wallet, straight to the creator's wallet ({addr}). You need a tiny bit of BNB for the fee: less than 1 cent.",
    'f.legal': 'This is a purchase of in-game items to support development. It is not an investment: it gives no right to tokens or profits.',
    'f.stage.wallet': 'Confirm in your wallet…',
    'f.stage.confirm': 'Waiting for the network…',
    'f.badHash': 'That is not a valid transaction hash.',
    'f.notFound': 'That transaction was not found on BNB Chain.',
    'f.err.pending': 'The transaction is not confirmed yet. Try again in a minute.',
    'f.err.failed': 'That transaction failed on the network.',
    'f.err.notToTreasury': 'That transaction is not a payment to RIFTFALL.',
    'f.err.tooLow': 'The amount is too low for any tier.',
    'f.err.otherPayer': 'That payment was made by another wallet.',
    'f.err.offline': 'No server connection. Keep your receipt if you already paid.',
    'f.err.priceUnavailable': 'Could not fix the amount. Try again before paying.',
    'f.err.signature': 'Your signature could not be verified. Connect your wallet again.',
    'f.err.session': 'Sign in to your Rift Account again before continuing.',
    'f.err.otherItem': 'That payment is for another item.',
    'f.err.tooManyOrders': 'You reached today’s order limit. Try tomorrow.',
    'f.err.orderExpired': 'The order expired before the payment was confirmed. Contact the owner with your receipt.',
    'f.err.badOrder': 'The payment does not match the order. Keep your receipt.',
    'f.err.manualReview': 'This previous payment needs owner review. Keep your receipt.',
    'f.err.accountChanged': 'You signed in to your other account. Start the purchase again after it loads.',
    'f.err.wrongChain': 'Could not check the payment on BNB Chain. Do not pay again.',
    'f.err.claimed': 'That receipt was already recognized on an account.',
    'f.err.otherItem': 'That payment is for another purchase (a Rift Cargo cosmetic), not the Founder Pass.',
    'acct.guest': 'Guest',
    'acct.title': 'Account',
    'f.priceErr': 'BNB price unavailable',
    'menu.riftLevel': 'RIFT LEVEL',
    'menu.riftPrev': 'Previous level',
    'menu.riftNext': 'Next level',
    'rift.normal': 'The Rift as it is',
    'rift.mods': 'Enemies +{hp}% HP · +{dmg}% damage',
    'rift.reward': 'Reward x{r}',
    'rift.locked': 'Win level {n} to unlock {m}',
    'menu.play': 'PLAY',
    'menu.arena': 'ARENA',
    'menu.hintKeys': 'WASD, arrows or the mouse to move · weapons fire on their own · 1-2-3 to pick upgrades',
    'menu.hintTouch': 'Drag your finger anywhere to move · weapons fire on their own',
    'menu.daily': 'Daily missions',
    'menu.streak': 'Streak {n}🔥',
    'menu.footer': 'Every run is re-simulated before rewards are paid.',
    'menu.cargo': 'New: Rift Cargo, run your own space freight company →',
    'nav.hangar': 'Hangar',
    'nav.market': 'Market',
    'nav.ranking': 'Ranking',
    'nav.vault': 'Redeem',
    'nav.economy': 'Economy',
    'nav.talents': 'Skills',
    'net.connecting': 'Connecting…',
    'net.online': '● Online · {net} network ({id})',
    'net.onlineNoChain': '● Online · blockchain not configured (guest mode)',
    'net.shop': '● Ship shop open · runs in practice mode',
    'net.shopTest': '● Test network · tokens and ships have no real value',
    'net.practice': '● Practice mode · your progress is saved on this device',

    'sheet.close': 'Close',
    'sheet.loading': 'Loading…',

    'lvl.kicker': 'LEVEL UP',
    'lvl.title': 'LEVEL {n}',
    'lvl.chestKicker': 'BOSS LOOT',
    'lvl.chestTitle': "GUARDIAN'S CHEST",
    'lvl.hint': 'Pick with a click, a tap or the keys 1 · 2 · 3',
    'lvl.new': 'NEW',
    'lvl.evo': 'EVOLUTION',
    'choice.evoText': '{desc}. Evolves your {weapon}.',
    'choice.repair': 'Repair kit',
    'choice.repairText': 'Restores 40% of your HP',
    'choice.cache': 'Shard cache',
    'choice.cacheText': '+2 Shards',

    'pause.title': 'PAUSED',
    'pause.resume': 'Resume',
    'pause.quit': 'End run',

    'go.kicker': 'RUN OVER',
    'go.victory': 'VICTORY',
    'go.titleVictory': 'RIFT SEALED',
    'go.titleRetreat': 'RETREAT',
    'go.titleDead': 'SHIP DESTROYED',
    'go.time': 'TIME',
    'go.kills': 'KILLS',
    'go.combo': 'MAX COMBO',
    'go.score': 'SCORE',
    'go.verifying': 'Verifying the run on the server (full re-simulation)…',
    'go.verified': '✔ Run verified: the server re-simulated every tick and got the same result.',
    'go.practice': 'Practice mode: your progress is saved on this device.',
    'go.shardsEarned': 'SHARDS EARNED',
    'go.coresEarned': 'CORES EARNED',
    'go.again': 'PLAY AGAIN',
    'go.menu': 'Menu',
    'go.share': 'Share',
    'go.talents': 'Skills',
    'go.rowRun': 'Run loot (x{m} from your ship)',
    'go.rowStreak': 'Daily streak bonus',
    'go.rowMission': 'Mission: {name}',
    'go.rowArena': 'Score recorded in the Arena',
    'go.rowCores': 'Cores from this run',
    'go.newBest': 'NEW RECORD!',
    'go.riftUnlocked': 'Rift level {n} unlocked',
    'go.titleCollapse': 'THE RIFT COLLAPSED',
    'go.canUpgrade': 'You have enough Cores to upgrade a skill',

    'ann.survive': 'SURVIVE',
    'ann.surviveSub': 'the Rift is opening',
    'ann.arena': 'ARENA',
    'ann.arenaSub': 'the best score takes the pot',
    'ann.boss': '⚠ RIFT GUARDIAN',
    'ann.bossSub': 'incoming',
    'ann.swarm': 'SWARM',
    'ann.swarmSub': "you're being surrounded",
    'ann.bossDead': 'GUARDIAN DEFEATED',
    'ann.bossDeadSub': 'grab its chest',
    'ann.evolved': 'weapon evolved',
    'ann.final': '⚠ THE RIFT HEART AWAKENS',
    'ann.finalSub': 'destroy it to win',
    'ann.meteors': 'METEOR SHOWER',
    'ann.meteorsSub': 'dodge the red circles · they hurt enemies too',
    'ann.elites': 'ELITE SQUAD',
    'ann.elitesSub': 'three elites incoming',

    'tut.moveTouch': '<b>Drag your finger</b> anywhere to move.',
    'tut.moveKeys': 'Move with <b>WASD</b>, the <b>arrow keys</b> or the <b>mouse</b>.',
    'tut.auto': 'Your weapons <b>fire on their own</b>. Just dodge and survive.',
    'tut.gems': 'Collect <b>crystals</b> to level up and pick upgrades.',
    'tut.evo': 'Weapon at <b>level 5</b> + its paired upgrade = <b>EVOLUTION</b>. Bosses drop chests.',
    'tut.final': 'At <b>10:00</b> the <b>Rift Heart</b> awakens: destroy it to win.',

    'toast.practice': 'Practice mode: you earn Cores for your skills. Shards arrive with the rewards server.',
    'toast.walletConnected': 'Wallet connected: {a}',
    'toast.noChain': 'The blockchain is not configured on this server. You can play as a guest.',
    'toast.transferred': 'Done: your progress arrived in this browser.',
    'bridge.got': 'Rift Route: +{n} Cores for your company in Rift Cargo',
    'bridge.next': 'level {lv} there = +{n} Cores here',
    'mv.title': 'Your progress on another device',
    'mv.text': 'Open this link on your other phone or computer to get the same progress and ranking name. It is yours: do not share it with other players.',
    'mv.btn': 'Copy my progress link',
    'mv.copied': 'Link copied. Paste it on the other device.',

    'fx.heal': '+HP',
    'fx.combo': 'COMBO x{n}!',

    'err.unknown': 'Unknown error',
    'err.rejected': 'Cancelled in the wallet',
    'err.pending': 'MetaMask already has a request waiting for you: open it, accept or reject it and try again.',
    'err.funds': 'Not enough funds to pay for the operation and gas',
    'err.network': 'The network rejected the operation. Check your balance and that your wallet is on the game’s network.',
    'err.connectFirst': 'Connect your wallet first',
    'err.noWallet': 'No wallet detected. Install MetaMask, Rabby or Coinbase Wallet.',

    'ship.yield': 'Rewards x{m}',

    'tal.kicker': 'PILOT',
    'tal.title': 'Skills',
    'tal.intro': 'Permanent upgrades that apply to every run (except the Arena, where everyone competes equally). Buy them with Cores ✦, earned by playing: kills, time, Guardians, Shards collected and missions.',
    'tal.local': 'Your progress is saved on this device.',
    'tal.cores': 'YOUR CORES',
    'tal.perLevel': '{desc} per level',
    'tal.upgrade': 'Upgrade · {c} ✦',
    'tal.max': 'MAXED',
    'tal.done': '{name} reached level {n}',
    'tal.poor': 'Not enough Cores: play more runs',
    'tal.note': 'Cores cannot be exchanged for tokens: they only make your pilot stronger.',
    'tal.stats': 'Your stats',

    'st.runs': 'RUNS',
    'st.best': 'BEST SCORE',
    'st.bestTime': 'BEST TIME',
    'st.kills': 'KILLS',
    'st.bosses': 'GUARDIANS',
    'st.victories': 'VICTORIES',
    'st.lifetimeShards': 'TOTAL SHARDS',
    'st.streak': 'STREAK',
    'st.days': '{n} days',
    'st.day': '1 day',

    'h.kicker': 'SHIPS',
    'h.title': 'Hangar',
    'h.intro': 'Your ship sets your starting weapon, your stats and your Shard multiplier. Each Forge level adds +2% damage and +3% rewards.',
    'h.yours': 'Your ships',
    'h.connectMine': 'Connect your wallet to see and fly your NFT ships.',
    'ship.needWallet': 'Connect your wallet to fly this ship.',
    'toast.sparkFallback': 'Without your wallet connected you cannot fly the {name}: this run uses the SPARK.',
    'h.free': 'FREE',
    'h.inUse': 'In use',
    'h.use': 'Use',
    'h.demo': 'DEMO',
    'h.try': 'Try',
    'h.forge': 'Forge LV {n} · {c} {sym}',
    'h.forging': 'Forging…',
    'h.forged': 'Ship #{id} reached level {n}!',
    'h.sell': 'Sell',
    'h.sellPrompt': 'Sale price in {sym}:',
    'h.publishing': 'Listing…',
    'h.published': 'Ship listed on the Market',
    'h.shipyard': 'Shipyard · NFT ships',
    'h.connect': 'Connect your wallet to buy NFT ships. They are yours: use them, forge them or sell them on the Market.',
    'h.buying': 'Buying…',
    'h.bought': '{name} is yours!',
    'h.left': '{n} of {m} available',
    'h.soldOut': 'SOLD OUT',
    'h.noFunds': 'Not enough: you have {have} {sym} and this ship costs {price} {sym}.',
    'h.payRift': 'You can pay with RIFT (gold button).',
    'h.faucet': 'Test tBNB is free from a faucet.',
    'o.title': 'Owner panel (only you see this)',
    'o.intro': 'Change each ship’s price. In RIFT, 0 means the ship is not sold for RIFT. Each change is a transaction you sign in your wallet (costs cents of {sym}).',
    'o.native': 'Price in {sym}',
    'o.rift': 'Price in RIFT',
    'o.save': 'Save',
    'o.saving': 'Confirming…',
    'o.saved': '{name} price updated',
    'o.withdraw': 'Collect {v} {sym} in sales',
    'o.withdrawn': 'Sales collected to your wallet',
    'o.invalid': 'Invalid price: use numbers only (e.g. 50000 or 0.015)',
    'h.split': 'RIFT payments are split: 40% is burned, 30% returns to the players’ reward pool and 30% goes to the project treasury.',
    'h.ready': '{name} ready for launch',
    'h.noShop': 'The NFT ship shop opens with the token launch. Meanwhile, the free SPARK can win the run.',
    'h.testnet': 'Test network: tBNB and this version’s tokens have no real value. They are for trying the shop, forge and market before launch.',

    'n.noChain': 'NFT ships, the market and redemptions open with the token launch. Meanwhile you can play free with the SPARK and upgrade your skills.',

    'm.kicker': 'P2P',
    'm.title': 'Market',
    'm.connect': 'Connect your wallet to buy and sell ships with other pilots.',
    'm.intro': 'Buy ships from other players with RIFT. Market fee: {fee}% (paid by the seller). Non-custodial: the ship stays in the seller’s wallet until it sells.',
    'm.empty': 'No ships for sale right now. List yours from the Hangar.',
    'm.withdraw': 'Unlist',
    'm.buy': 'Buy · {p} {sym}',
    'm.withdrawing': 'Unlisting…',
    'm.withdrawn': 'Listing removed',
    'm.bought': 'You bought ship #{id}!',
    'm.seller': 'Seller {a}',

    'a.kicker': 'TOURNAMENTS',
    'a.title': 'Arena',
    'a.server': 'Arena tournaments turn on when the rewards server is online.',
    'a.intro': 'Pay the entry fee in RIFT and compete for the pot. Everyone flies the SPARK with no pilot skills (only how you play counts). Your best score counts; play as many times as you want until it closes.',
    'a.none': 'No tournament is open right now. Come back soon!',
    'a.pool': 'POT',
    'a.fee': 'ENTRY',
    'a.pilots': 'PILOTS',
    'a.closes': 'CLOSES IN',
    'a.split': 'From the pot: {rake}% to the project, {burn}% is burned and the rest is split among the top {top} (1st: {first}%). Anything unassigned returns to the reward pool.',
    'a.connect': 'Connect your wallet to enter the Arena.',
    'a.play': 'PLAY ARENA',
    'a.enter': 'Enter · {fee} {sym}',
    'a.entering': 'Entering…',
    'a.entered': "You're in! You can play the Arena now",
    'a.standings': 'Standings',
    'a.noScores': 'No scores in this tournament yet. The first one sets the pace!',
    'a.past': 'Past tournaments',
    'a.pastRow': '#{id} · winner: {name} ({score} pts)',

    'r.kicker': 'TOP PILOTS',
    'r.title': 'Ranking',
    'r.server': 'The ranking turns on when the rewards server is online.',
    'r.empty': 'No runs yet. Be the first!',
    'r.today': 'TODAY',
    'r.all': 'ALL TIME',
    'r.pilot': 'Pilot',
    'r.ship': 'Ship',
    'r.time': 'Time',
    'r.score': 'Score',

    'v.kicker': 'SHARDS → $RIFT',
    'v.title': 'Redeem',
    'v.server': 'Redeeming Shards for the token turns on with the launch and the rewards server. Meanwhile, play to upgrade your skills.',
    'v.yours': 'YOUR SHARDS',
    'v.rate': 'RATE',
    'v.min': 'MINIMUM',
    'v.balance': 'RIFT BALANCE',
    'v.connect': 'Connect your wallet to turn your Shards into real $RIFT tokens.',
    'v.today': 'Today’s emission: {left} of {budget} RIFT left. Per-player limit: {cap} RIFT/day.',
    'v.claim': 'Redeem',
    'v.signing': 'Signing…',
    'v.signed': 'Voucher signed by the server. Confirm the transaction in your wallet.',
    'v.received': '$RIFT received in your wallet!',
    'v.voucher': 'The server signs a voucher (EIP-712) valid for 1 hour. If you don’t claim it, it expires and the Shards return to your account automatically.',
    'v.history': 'History',
    'v.pending': 'PENDING',
    'v.paid': 'CLAIMED',
    'v.expired': 'EXPIRED',
    'v.collect': 'Claim',
    'v.collecting': 'Claiming…',
    'v.receivedShort': '$RIFT received!',

    'e.kicker': 'TRANSPARENCY',
    'e.title': '$RIFT economy',
    'e.supply': '$RIFT has a fixed supply of 1,000,000,000. The contract cannot mint more tokens, charges no transfer tax and nobody can freeze your balance.',
    'e.alloc': 'Initial distribution',
    'e.a1': 'Player rewards (vault with halving)',
    'e.a2': 'Treasury (marketing, prize pools, partnerships)',
    'e.a3': 'DEX liquidity',
    'e.a4': 'Team (locked: 6 months + 24 linear)',
    'e.a5': 'Community and airdrops',
    'e.emission': 'Player emission',
    'e.budget': 'TODAY’S BUDGET',
    'e.remaining': 'LEFT TODAY',
    'e.vault': 'IN THE VAULT',
    'e.cap': 'CAP/PLAYER',
    'e.halving': 'The daily emission halves every 180 days. Part of what is spent on the Forge, RIFT purchases and tournaments returns to the vault, extending its life.',
    'e.where': 'Where each RIFT spent goes',
    'e.action': 'Action',
    'e.burn': 'Burn',
    'e.pool': 'Player pool',
    'e.project': 'Project',
    'e.rowForge': 'Forge / ship purchases in RIFT',
    'e.rowArena': 'Arena entry',
    'e.rowArenaPool': 'unassigned prizes',
    'e.rowMarket': 'Market sale',
    'e.rowMarketFee': '5% fee',
    'e.activity': 'Activity',
    'e.players': 'PILOTS',
    'e.verified': 'VERIFIED RUNS',
    'e.rejected': 'REJECTED',
    'e.issued': 'SHARDS ISSUED',
    'e.contracts': 'Contracts',
    'e.watch': 'Show {sym} in my wallet',

    'p.save': 'Save',
    'p.saved': 'Name updated',
    'p.wallet': 'Linked wallet: {a}',
    'p.guest': 'You are playing as a guest. Connect a wallet to keep your progress in it and redeem Shards.',
    'p.connect': 'Connect wallet',
    'p.noServer': 'No connection to the server.',

    'sh.text': 'I survived {time} in RIFTFALL: {kills} kills and {score} points 🚀 Can you beat me?',
    'sh.textWin': 'I sealed the Rift in RIFTFALL! {kills} kills and {score} points 🏆 Can you beat me?',
    'sh.cta': 'Play free in your browser',
    'sh.copied': 'Text copied: paste it on WhatsApp or anywhere',
    'sh.ship': 'SHIP'
  },

  pt: {
    'hud.hull': 'CASCO',
    'hud.kills': 'ABATES',
    'hud.shards': 'SHARDS',
    'hud.boss': 'GUARDIÃO DO RIFT',
    'hud.final': 'CORAÇÃO DO RIFT',
    'hud.rift': 'RIFT {n}',
    'hud.combo': 'COMBO',
    'hud.pause': 'Pausa',
    'hud.level': 'NV {n}',

    'menu.shardsTitle': 'Shards (moeda do jogo)',
    'menu.coresTitle': 'Núcleos (para melhorar suas habilidades)',
    'menu.riftTitle': 'Saldo de $RIFT',
    'menu.connect': 'Conectar carteira',
    'menu.connecting': 'Conectando…',
    'menu.walletNoNet': 'Carteira (sem rede)',
    'menu.sound': 'Som',
    'menu.lang': 'Idioma',
    'menu.season': 'TEMPORADA 1 · PROOF OF PLAY',
    'menu.tagline': 'Sobreviva à fenda. Derrote os Guardiões. Melhore as habilidades do seu piloto e ganhe <b>Shards</b>.',
    'menu.selectedShip': 'NAVE SELECIONADA',
    'menu.change': 'Trocar',
    'nav.workshop': 'Oficina',
    'pt.kicker': 'SUA NAVE, DO SEU JEITO',
    'pt.title': 'Oficina',
    'pt.intro': 'Monte sua nave com peças de qualquer design: elas mudam o visual e dão bônus. Peças repetidas sobem de nível. Saem das caixas que os Guardiões soltam e ao vencer.',
    'pt.slot.hull': 'Fuselagem',
    'pt.slot.wings': 'Asas',
    'pt.slot.engines': 'Motores',
    'pt.slot.cockpit': 'Cabine',
    'pt.name': '{slot} {ship}',
    'pt.new': 'Peça nova: {part}',
    'pt.up': '{part} sobe para Nv {n}',
    'pt.maxed': '{part} já está no máximo',
    'pt.crate': 'Abrir caixa',
    'pt.crateCost': 'Caixa de peças · {c} ✦',
    'pt.poor': 'Faltam Núcleos para a caixa.',
    'pt.empty': 'Original',
    'pt.locked': 'Não descoberta',
    'pt.equip': 'Equipada',
    'pt.bonus': 'Bônus das suas peças',
    'pt.none': 'Você ainda não tem peças equipadas.',
    'pt.note': 'As peças não valem na Arena nem no Desafio do Dia: lá todos competem de igual para igual.',
    'pt.stat.speed': '+{v}% velocidade',
    'pt.stat.maxHp': '+{v}% casco',
    'pt.stat.crit': '+{v}% crítico',
    'pt.stat.area': '+{v}% área',
    'pt.stat.armor': '+{v} blindagem',
    'pt.stat.magnet': '+{v}% ímã',
    'pt.stat.might': '+{v}% dano',
    'pt.stat.cooldown': '-{v}% recarga',
    'pt.stat.regen': '+{v} reparo/s',
    'pt.stat.xpGain': '+{v}% experiência',
    'rv.title': 'CONTINUAR VOANDO?',
    'rv.text': 'Assista a um anúncio e volte com metade do casco.',
    'rv.watch': 'Ver anúncio e reviver',
    'rv.no': 'Não, obrigado',
    'rv.double': 'x2 Núcleos',
    'rv.doubled': 'Núcleos dobrados! +{n} ✦',
    'menu.taglinePortal': 'Sobreviva à fenda. Derrote os Guardiões e o <b>Coração do Rift</b>. Melhore as habilidades do seu piloto.',
    'dc.title': 'DESAFIO DO DIA',
    'wd.title': 'RANKING MUNDIAL DE HOJE',
    'wd.none': 'Ninguém entrou no ranking de hoje ainda: seja o primeiro!',
    'wd.you': 'Sua posição: <b>#{n}</b>',
    'wd.out': 'Para entrar no top {max} são necessários <b>{score}</b> pontos.',
    'wd.as': '✎ {name}',
    'wd.prompt': 'Seu nome de piloto para o ranking (máximo 16 letras):',
    'wd.checking': '🌍 Ranking mundial: verificando a partida…',
    'wd.rank': '🌍 #{n} do mundo hoje!',
    'wd.kept': '🌍 Sua melhor marca de hoje continua em #{n}',
    'wd.outRow': '🌍 Fora do top {max} (o último tem {score})',
    'wd.err': '🌍 Não foi possível enviar ao ranking mundial',
    'rk.title': 'RANKING DE HOJE',
    'rk.more': 'VER RANKING COMPLETO',
    'rk.none': 'Ninguém jogou hoje ainda: sua partida pode ser a primeira!',
    'rk.play': 'Jogue uma partida e sua melhor pontuação de hoje aparece aqui para todos.',
    'rk.youAre': 'Você está em #{n}.',
    'rk.intro': 'Todas as partidas de todos os jogadores, verificadas pelo site do jogo. Cada jogador aparece com sua melhor pontuação.',
    'rk.checking': '🏆 Ranking: verificando a partida…',
    'rk.rank': '🏆 #{n} no ranking de hoje!',
    'rk.out': '🏆 Fora do top {max} de hoje',
    'rk.allRank': '#{n} histórico',
    'rk.err': '🏆 Não foi possível carregar o ranking. Tente de novo daqui a pouco.',
    'rk.sendErr': '🏆 Não foi possível enviar a partida ao ranking',
    'dc.rules': 'Todos jogam a mesma partida: nave SPARK, sem habilidades, Rift 1.',
    'dc.best': 'Seu melhor hoje: {score} pts · {time}',
    'dc.tries': '{n} tentativas',
    'dc.next': 'Novo desafio em {h} h {m} min',
    'dc.play': 'JOGAR DESAFIO',
    'dc.again': 'Melhorar marca',
    'dc.hud': 'DESAFIO #{n}',
    'dc.newBest': 'Sua melhor marca no desafio #{n}!',
    'dc.row': 'Desafio #{n} · seu melhor de hoje',
    'ann.daily': 'DESAFIO #{n}',
    'ann.dailySub': 'a mesma partida para o mundo todo',
    'r.challenge': 'DESAFIO',
    'sh.daily': 'RIFTFALL · Desafio #{n} ⏱ {time} · 💥 {kills} · ★ {score} Consegue me superar?',
    'du.title': 'DUELO COM AMIGOS',
    'du.text': 'Jogue uma partida e envie o link: seu amigo joga o mesmo mapa com a mesma nave. Vence quem fizer mais pontos.',
    'du.play': 'JOGAR E DESAFIAR',
    'du.from': '⚔ {name} desafiou você para um duelo',
    'du.fromAnon': '⚔ Um amigo desafiou você para um duelo',
    'du.mark': '{score} pts · {time} · {kills} abates',
    'du.sub': 'Mesmo mapa, mesma nave. Consegue vencer?',
    'du.accept': 'ACEITAR DUELO',
    'du.hud': 'DUELO',
    'du.vs': 'contra {name} · {score} pts',
    'du.new': 'faça sua marca e depois desafie um amigo',
    'du.friend': 'seu amigo',
    'du.won': 'Você venceu {name} por {diff} pontos!',
    'du.lost': '{name} venceu você por {diff} pontos',
    'du.tie': 'Empate com {name}!',
    'du.ready': 'Envie o link e seu amigo jogará este mesmo mapa.',
    'du.name': 'Seu nome (opcional)',
    'du.send': '⚔ DESAFIAR UM AMIGO',
    'du.sendBack': '⚔ DEVOLVER O DESAFIO',
    'du.again': 'MESMO MAPA',
    'du.bad': 'O link do duelo está incompleto ou foi modificado.',
    'sh.duel': '⚔ Te desafio para um duelo em RIFTFALL: fiz {score} pontos e aguentei {time}. Mesmo mapa, mesma nave. Consegue me vencer?',
    'pwa.install': '⬇ Instalar',
    'pwa.ios': 'No iPhone: toque Compartilhar e depois «Adicionar à Tela de Início».',
    'f.kicker': 'APOIE O JOGO',
    'f.title': 'Passe Fundador',
    'f.banner': '★ PASSE FUNDADOR',
    'up.text': 'Curtindo RIFTFALL? Vire Fundador a partir de US$3: rastro dourado, pinturas exclusivas e a LEVIATHAN no lançamento.',
    'up.cta': '★ VER PASSE FUNDADOR',
    'up.no': 'Agora não',
    'f.bannerSub': 'Apoie o RIFTFALL antes do lançamento · a partir de US$3',
    'f.badge': 'FUNDADOR',
    'f.intro': 'O RIFTFALL é feito por uma pessoa só, sem investidores. Com o Passe Fundador você ajuda a lançá-lo na rede principal e ganha itens exclusivos para sempre.',
    'f.tier.pilot': 'Piloto Fundador',
    'f.tier.gold': 'Fundador Ouro',
    'f.tier.legend': 'Fundador Lenda',
    'f.perk.badge': 'Insígnia ★ Fundador',
    'f.perk.trail': 'Rastro dourado para sua nave',
    'f.perk.prev': 'Tudo do nível anterior',
    'f.perk.skinGold': 'Pintura dourada para todas as naves',
    'f.perk.skinPrisma': 'Pintura «Prisma» exclusiva',
    'f.perk.leviathan': 'Nave LEVIATHAN (NFT) na sua carteira quando o jogo for para a rede principal',
    'f.payUsdt': 'Pagar {n} USDT',
    'f.payBnb': 'Pagar ≈{n} BNB',
    'f.owned': '✓ Já é seu',
    'f.youAre': 'Você é {tier}',
    'f.viewTx': 'Ver o pagamento no BscScan',
    'f.owner': 'Dono do jogo · tudo desbloqueado',
    'f.viewWallet': 'Ver a carteira do criador no BscScan',
    'f.skin': 'Pintura',
    'f.skin.original': 'Original',
    'f.skin.founder': 'Dourada',
    'f.skin.prisma': 'Prisma',
    'f.restoreTitle': 'Já pagou em outro dispositivo?',
    'f.restoreHint': 'Cole o hash da transação (começa com 0x) e toque em Verificar.',
    'f.verify': 'Verificar pagamento',
    'f.verified': 'Obrigado! Agora você é {tier}',
    'f.howTo': 'O pagamento é na BNB Smart Chain (rede principal) pela sua carteira e vai direto para a carteira do criador ({addr}). Você precisa de um pouquinho de BNB para a taxa: menos de 1 centavo.',
    'f.legal': 'É uma compra de itens do jogo para apoiar o desenvolvimento. Não é um investimento: não dá direito a tokens nem a lucros.',
    'f.stage.wallet': 'Confirme na sua carteira…',
    'f.stage.confirm': 'Esperando a rede…',
    'f.badHash': 'Esse não é um hash de transação válido.',
    'f.notFound': 'Não encontramos essa transação na BNB Chain.',
    'f.err.pending': 'A transação ainda não foi confirmada. Tente em um minuto.',
    'f.err.failed': 'Essa transação falhou na rede.',
    'f.err.notToTreasury': 'Essa transação não é um pagamento ao RIFTFALL.',
    'f.err.tooLow': 'O valor não alcança nenhum nível.',
    'f.err.otherPayer': 'Esse pagamento foi feito por outra carteira.',
    'f.err.offline': 'Sem conexão com o servidor. Guarde o comprovante se já pagou.',
    'f.err.priceUnavailable': 'Não foi possível fixar o valor. Tente novamente antes de pagar.',
    'f.err.signature': 'Não foi possível verificar sua assinatura. Conecte a carteira novamente.',
    'f.err.session': 'Entre novamente na sua Conta Rift antes de continuar.',
    'f.err.otherItem': 'Esse pagamento corresponde a outro artigo.',
    'f.err.tooManyOrders': 'Você atingiu o limite de pedidos de hoje. Tente amanhã.',
    'f.err.orderExpired': 'O pedido venceu antes da confirmação do pagamento. Contate o dono com o comprovante.',
    'f.err.badOrder': 'O pagamento não corresponde ao pedido. Guarde o comprovante.',
    'f.err.manualReview': 'Esse pagamento anterior precisa de revisão do dono. Guarde o comprovante.',
    'f.err.accountChanged': 'Você entrou na outra conta. Reinicie a compra quando terminar de carregar.',
    'f.err.wrongChain': 'Não foi possível conferir o pagamento na BNB Chain. Não pague novamente.',
    'f.err.claimed': 'Esse comprovante já foi reconhecido em uma conta.',
    'f.err.otherItem': 'Esse pagamento é de outra compra (um item visual do Rift Cargo), não do Passe Fundador.',
    'acct.guest': 'Convidado',
    'acct.title': 'Conta',
    'f.priceErr': 'Preço do BNB indisponível',
    'menu.riftLevel': 'NÍVEL DO RIFT',
    'menu.riftPrev': 'Nível anterior',
    'menu.riftNext': 'Próximo nível',
    'rift.normal': 'O Rift como ele é',
    'rift.mods': 'Inimigos +{hp}% vida · +{dmg}% dano',
    'rift.reward': 'Prêmio x{r}',
    'rift.locked': 'Vença o nível {n} para desbloquear o {m}',
    'menu.play': 'JOGAR',
    'menu.arena': 'ARENA',
    'menu.hintKeys': 'WASD, setas ou o mouse para mover · as armas atiram sozinhas · 1-2-3 para escolher melhorias',
    'menu.hintTouch': 'Arraste o dedo em qualquer lugar para mover · as armas atiram sozinhas',
    'menu.daily': 'Missões diárias',
    'menu.streak': 'Sequência {n}🔥',
    'menu.footer': 'Cada partida é re-simulada antes de pagar recompensas.',
    'menu.cargo': 'Novo: Rift Cargo, sua empresa de transporte espacial →',
    'nav.hangar': 'Hangar',
    'nav.market': 'Mercado',
    'nav.ranking': 'Ranking',
    'nav.vault': 'Resgatar',
    'nav.economy': 'Economia',
    'nav.talents': 'Habilidades',
    'net.connecting': 'Conectando…',
    'net.online': '● Online · rede {net} ({id})',
    'net.onlineNoChain': '● Online · blockchain não configurada (modo convidado)',
    'net.shop': '● Loja de naves aberta · partidas em modo treino',
    'net.shopTest': '● Rede de testes · tokens e naves sem valor real',
    'net.practice': '● Modo treino · seu progresso fica salvo neste aparelho',

    'sheet.close': 'Fechar',
    'sheet.loading': 'Carregando…',

    'lvl.kicker': 'VOCÊ SUBIU DE NÍVEL',
    'lvl.title': 'NÍVEL {n}',
    'lvl.chestKicker': 'SAQUE DE CHEFE',
    'lvl.chestTitle': 'BAÚ DO GUARDIÃO',
    'lvl.hint': 'Escolha com clique, toque ou as teclas 1 · 2 · 3',
    'lvl.new': 'NOVO',
    'lvl.evo': 'EVOLUÇÃO',
    'choice.evoText': '{desc}. Evolui sua {weapon}.',
    'choice.repair': 'Kit de reparo',
    'choice.repairText': 'Recupera 40% da vida',
    'choice.cache': 'Esconderijo de Shards',
    'choice.cacheText': '+2 Shards',

    'pause.title': 'PAUSA',
    'pause.resume': 'Continuar',
    'pause.quit': 'Encerrar partida',

    'go.kicker': 'FIM DA PARTIDA',
    'go.victory': 'VITÓRIA',
    'go.titleVictory': 'RIFT SELADO',
    'go.titleRetreat': 'RETIRADA',
    'go.titleDead': 'NAVE DESTRUÍDA',
    'go.time': 'TEMPO',
    'go.kills': 'ABATES',
    'go.combo': 'COMBO MÁX',
    'go.score': 'PONTOS',
    'go.verifying': 'Verificando a partida no servidor (re-simulação completa)…',
    'go.verified': '✔ Partida verificada: o servidor re-simulou cada tick e obteve o mesmo resultado.',
    'go.practice': 'Modo treino: seu progresso fica salvo neste aparelho.',
    'go.shardsEarned': 'SHARDS GANHOS',
    'go.coresEarned': 'NÚCLEOS GANHOS',
    'go.again': 'JOGAR DE NOVO',
    'go.menu': 'Menu',
    'go.share': 'Compartilhar',
    'go.talents': 'Habilidades',
    'go.rowRun': 'Saque da partida (x{m} pela sua nave)',
    'go.rowStreak': 'Bônus de sequência diária',
    'go.rowMission': 'Missão: {name}',
    'go.rowArena': 'Pontuação registrada na Arena',
    'go.rowCores': 'Núcleos da partida',
    'go.newBest': 'NOVO RECORDE!',
    'go.riftUnlocked': 'Nível do Rift {n} desbloqueado',
    'go.titleCollapse': 'O RIFT COLAPSOU',
    'go.canUpgrade': 'Você tem Núcleos para melhorar uma habilidade',

    'ann.survive': 'SOBREVIVA',
    'ann.surviveSub': 'o Rift está se abrindo',
    'ann.arena': 'ARENA',
    'ann.arenaSub': 'a melhor pontuação leva o prêmio',
    'ann.boss': '⚠ GUARDIÃO DO RIFT',
    'ann.bossSub': 'se aproximando',
    'ann.swarm': 'ENXAME',
    'ann.swarmSub': 'estão te cercando',
    'ann.bossDead': 'GUARDIÃO DERROTADO',
    'ann.bossDeadSub': 'pegue o baú',
    'ann.evolved': 'arma evoluída',
    'ann.final': '⚠ O CORAÇÃO DO RIFT DESPERTA',
    'ann.finalSub': 'destrua-o para vencer',
    'ann.meteors': 'CHUVA DE METEOROS',
    'ann.meteorsSub': 'desvie dos círculos vermelhos · eles também ferem os inimigos',
    'ann.elites': 'ESQUADRÃO DE ELITE',
    'ann.elitesSub': 'três inimigos de elite a caminho',

    'tut.moveTouch': '<b>Arraste o dedo</b> em qualquer lugar para mover.',
    'tut.moveKeys': 'Mova-se com <b>WASD</b>, as <b>setas</b> ou o <b>mouse</b>.',
    'tut.auto': 'Suas armas <b>atiram sozinhas</b>. Você só desvia e sobrevive.',
    'tut.gems': 'Pegue os <b>cristais</b> para subir de nível e escolher melhorias.',
    'tut.evo': 'Arma no <b>nível 5</b> + sua melhoria par = <b>EVOLUÇÃO</b>. Os chefes soltam baús.',
    'tut.final': 'Aos <b>10:00</b> desperta o <b>Coração do Rift</b>: destrua-o para vencer.',

    'toast.practice': 'Modo treino: você ganha Núcleos para suas habilidades. Os Shards chegam com o servidor de recompensas.',
    'toast.walletConnected': 'Carteira conectada: {a}',
    'toast.noChain': 'A blockchain não está configurada neste servidor. Você pode jogar como convidado.',
    'toast.transferred': 'Pronto: seu progresso chegou a este navegador.',
    'bridge.got': 'Rota Rift: +{n} Núcleos pela sua empresa no Rift Cargo',
    'bridge.next': 'nível {lv} lá = +{n} Núcleos aqui',
    'mv.title': 'Seu progresso em outro dispositivo',
    'mv.text': 'Abra este link no outro celular ou computador e terá o mesmo progresso e o mesmo nome no ranking. É seu: não compartilhe com outros jogadores.',
    'mv.btn': 'Copiar link do meu progresso',
    'mv.copied': 'Link copiado. Cole no outro dispositivo.',

    'fx.heal': '+VIDA',
    'fx.combo': 'COMBO x{n}!',

    'err.unknown': 'Erro desconhecido',
    'err.rejected': 'Operação cancelada na carteira',
    'err.pending': 'A MetaMask já tem uma janela aberta esperando sua resposta: abra, aceite ou recuse e tente de novo.',
    'err.funds': 'Saldo insuficiente para pagar a operação e o gás',
    'err.network': 'A rede rejeitou a operação. Confira seu saldo e se a carteira está na rede do jogo.',
    'err.connectFirst': 'Conecte sua carteira primeiro',
    'err.noWallet': 'Nenhuma carteira detectada. Instale MetaMask, Rabby ou Coinbase Wallet.',

    'ship.yield': 'Recompensa x{m}',

    'tal.kicker': 'PILOTO',
    'tal.title': 'Habilidades',
    'tal.intro': 'Melhorias permanentes aplicadas em todas as partidas (menos na Arena, onde todos competem igual). Compre com Núcleos ✦, que você ganha jogando: abates, tempo, Guardiões, Shards coletados e missões.',
    'tal.local': 'Seu progresso fica salvo neste aparelho.',
    'tal.cores': 'SEUS NÚCLEOS',
    'tal.perLevel': '{desc} por nível',
    'tal.upgrade': 'Melhorar · {c} ✦',
    'tal.max': 'MÁXIMO',
    'tal.done': '{name} subiu para o nível {n}',
    'tal.poor': 'Faltam Núcleos: jogue mais partidas',
    'tal.note': 'Os Núcleos não são trocados por tokens: servem só para deixar seu piloto mais forte.',
    'tal.stats': 'Suas estatísticas',

    'st.runs': 'PARTIDAS',
    'st.best': 'MELHOR PONTUAÇÃO',
    'st.bestTime': 'MELHOR TEMPO',
    'st.kills': 'ABATES',
    'st.bosses': 'GUARDIÕES',
    'st.victories': 'VITÓRIAS',
    'st.lifetimeShards': 'SHARDS TOTAIS',
    'st.streak': 'SEQUÊNCIA',
    'st.days': '{n} dias',
    'st.day': '1 dia',

    'h.kicker': 'NAVES',
    'h.title': 'Hangar',
    'h.intro': 'A nave define sua arma inicial, seus atributos e o multiplicador de Shards. Cada nível de Forja soma +2% de dano e +3% de recompensa.',
    'h.yours': 'Suas naves',
    'h.connectMine': 'Conecte sua carteira para ver e usar suas naves NFT.',
    'ship.needWallet': 'Conecte sua carteira para voar com esta nave.',
    'toast.sparkFallback': 'Sem a carteira conectada você não pode usar a {name}: esta partida usa a SPARK.',
    'h.free': 'GRÁTIS',
    'h.inUse': 'Em uso',
    'h.use': 'Usar',
    'h.demo': 'DEMO',
    'h.try': 'Testar',
    'h.forge': 'Forjar NV {n} · {c} {sym}',
    'h.forging': 'Forjando…',
    'h.forged': 'A nave #{id} subiu para o nível {n}!',
    'h.sell': 'Vender',
    'h.sellPrompt': 'Preço de venda em {sym}:',
    'h.publishing': 'Publicando…',
    'h.published': 'Nave publicada no Mercado',
    'h.shipyard': 'Estaleiro · naves NFT',
    'h.connect': 'Conecte sua carteira para comprar naves NFT. Elas são suas: use, forje ou venda no Mercado.',
    'h.buying': 'Comprando…',
    'h.bought': '{name} é sua!',
    'h.left': '{n} de {m} disponíveis',
    'h.soldOut': 'ESGOTADA',
    'h.noFunds': 'Não dá: você tem {have} {sym} e esta nave custa {price} {sym}.',
    'h.payRift': 'Você pode pagar com RIFT (botão dourado).',
    'h.faucet': 'Os tBNB de teste são grátis em uma torneira (faucet).',
    'o.title': 'Painel do dono (só você vê)',
    'o.intro': 'Mude o preço de cada nave. Em RIFT, 0 significa que a nave não é vendida com RIFT. Cada mudança é uma transação que você assina na carteira (custa centavos de {sym}).',
    'o.native': 'Preço em {sym}',
    'o.rift': 'Preço em RIFT',
    'o.save': 'Salvar',
    'o.saving': 'Confirmando…',
    'o.saved': 'Preço de {name} atualizado',
    'o.withdraw': 'Receber {v} {sym} de vendas',
    'o.withdrawn': 'Vendas recebidas na sua carteira',
    'o.invalid': 'Preço inválido: use só números (ex.: 50000 ou 0.015)',
    'h.split': 'Os pagamentos em RIFT são divididos: 40% é queimado, 30% volta ao pool de recompensas dos jogadores e 30% vai para a tesouraria do projeto.',
    'h.ready': '{name} pronta para decolar',
    'h.noShop': 'A loja de naves NFT abre com o lançamento do token. Enquanto isso, a SPARK grátis pode vencer a partida.',
    'h.testnet': 'Rede de testes: os tBNB e os tokens desta versão não têm valor real. Servem para testar a loja, a forja e o mercado antes do lançamento.',

    'n.noChain': 'As naves NFT, o mercado e o resgate abrem com o lançamento do token. Enquanto isso, jogue grátis com a SPARK e melhore suas habilidades.',

    'm.kicker': 'P2P',
    'm.title': 'Mercado',
    'm.connect': 'Conecte sua carteira para comprar e vender naves com outros pilotos.',
    'm.intro': 'Compre naves de outros jogadores pagando em RIFT. Taxa do mercado: {fee}% (paga pelo vendedor). Sem custódia: a nave fica na carteira do vendedor até a venda.',
    'm.empty': 'Não há naves à venda agora. Publique a sua pelo Hangar.',
    'm.withdraw': 'Retirar',
    'm.buy': 'Comprar · {p} {sym}',
    'm.withdrawing': 'Retirando…',
    'm.withdrawn': 'Anúncio retirado',
    'm.bought': 'Você comprou a nave #{id}!',
    'm.seller': 'Vende {a}',

    'a.kicker': 'TORNEIOS',
    'a.title': 'Arena',
    'a.server': 'Os torneios da Arena são ativados quando o servidor de recompensas estiver online.',
    'a.intro': 'Pague a inscrição em RIFT e dispute o prêmio. Todos usam a nave SPARK e sem habilidades do piloto (só conta como você joga). Vale sua melhor pontuação; jogue quantas vezes quiser até o fechamento.',
    'a.none': 'Não há torneio aberto agora. Volte logo!',
    'a.pool': 'PRÊMIO',
    'a.fee': 'INSCRIÇÃO',
    'a.pilots': 'PILOTOS',
    'a.closes': 'FECHA EM',
    'a.split': 'Do prêmio: {rake}% para o projeto, {burn}% é queimado e o resto é dividido entre o top {top} (1º: {first}%). O que não for distribuído volta ao pool de recompensas.',
    'a.connect': 'Conecte sua carteira para se inscrever na Arena.',
    'a.play': 'JOGAR ARENA',
    'a.enter': 'Inscrever-me · {fee} {sym}',
    'a.entering': 'Inscrevendo…',
    'a.entered': 'Inscrito! Você já pode jogar a Arena',
    'a.standings': 'Classificação',
    'a.noScores': 'Ainda não há pontuações neste torneio. O primeiro dita o ritmo!',
    'a.past': 'Torneios anteriores',
    'a.pastRow': '#{id} · vencedor: {name} ({score} pts)',

    'r.kicker': 'TOP PILOTOS',
    'r.title': 'Ranking',
    'r.server': 'O ranking é ativado quando o servidor de recompensas estiver online.',
    'r.empty': 'Ainda não há partidas. Seja o primeiro!',
    'r.today': 'HOJE',
    'r.all': 'GERAL',
    'r.pilot': 'Piloto',
    'r.ship': 'Nave',
    'r.time': 'Tempo',
    'r.score': 'Pontos',

    'v.kicker': 'SHARDS → $RIFT',
    'v.title': 'Resgatar',
    'v.server': 'O resgate de Shards pelo token é ativado com o lançamento e o servidor de recompensas. Enquanto isso, jogue para melhorar suas habilidades.',
    'v.yours': 'SEUS SHARDS',
    'v.rate': 'TAXA',
    'v.min': 'MÍNIMO',
    'v.balance': 'SALDO RIFT',
    'v.connect': 'Conecte sua carteira para transformar seus Shards em tokens $RIFT reais.',
    'v.today': 'Emissão de hoje: restam {left} de {budget} RIFT. Limite por jogador: {cap} RIFT/dia.',
    'v.claim': 'Resgatar',
    'v.signing': 'Assinando…',
    'v.signed': 'Voucher assinado pelo servidor. Confirme a transação na sua carteira.',
    'v.received': '$RIFT recebidos na sua carteira!',
    'v.voucher': 'O servidor assina um voucher (EIP-712) válido por 1 hora. Se você não resgatar, ele vence e os Shards voltam automaticamente para sua conta.',
    'v.history': 'Histórico',
    'v.pending': 'PENDENTE',
    'v.paid': 'RESGATADO',
    'v.expired': 'VENCIDO',
    'v.collect': 'Resgatar',
    'v.collecting': 'Resgatando…',
    'v.receivedShort': '$RIFT recebidos!',

    'e.kicker': 'TRANSPARÊNCIA',
    'e.title': 'Economia $RIFT',
    'e.supply': 'O $RIFT tem oferta fixa de 1.000.000.000. O contrato não permite criar mais tokens, não cobra taxa de transferência e ninguém pode congelar seu saldo.',
    'e.alloc': 'Distribuição inicial',
    'e.a1': 'Recompensas dos jogadores (vault com halving)',
    'e.a2': 'Tesouraria (marketing, prêmios, parcerias)',
    'e.a3': 'Liquidez em DEX',
    'e.a4': 'Equipe (bloqueado: 6 meses + 24 linear)',
    'e.a5': 'Comunidade e airdrops',
    'e.emission': 'Emissão para jogadores',
    'e.budget': 'ORÇAMENTO HOJE',
    'e.remaining': 'RESTANTE HOJE',
    'e.vault': 'NO VAULT',
    'e.cap': 'LIMITE/JOGADOR',
    'e.halving': 'A emissão diária cai pela metade a cada 180 dias. Parte do que é gasto na Forja, em compras com RIFT e em torneios volta ao vault, prolongando sua vida.',
    'e.where': 'Para onde vai cada RIFT gasto',
    'e.action': 'Ação',
    'e.burn': 'Queima',
    'e.pool': 'Pool jogadores',
    'e.project': 'Projeto',
    'e.rowForge': 'Forja / compra de naves em RIFT',
    'e.rowArena': 'Inscrição da Arena',
    'e.rowArenaPool': 'prêmios não atribuídos',
    'e.rowMarket': 'Venda no Mercado',
    'e.rowMarketFee': '5% de taxa',
    'e.activity': 'Atividade',
    'e.players': 'PILOTOS',
    'e.verified': 'PARTIDAS VERIFICADAS',
    'e.rejected': 'REJEITADAS',
    'e.issued': 'SHARDS EMITIDOS',
    'e.contracts': 'Contratos',
    'e.watch': 'Ver {sym} na minha carteira',

    'p.save': 'Salvar',
    'p.saved': 'Nome atualizado',
    'p.wallet': 'Carteira vinculada: {a}',
    'p.guest': 'Você está jogando como convidado. Conecte uma carteira para guardar seu progresso nela e resgatar Shards.',
    'p.connect': 'Conectar carteira',
    'p.noServer': 'Sem conexão com o servidor.',

    'sh.text': 'Sobrevivi {time} no RIFTFALL: {kills} abates e {score} pontos 🚀 Consegue me superar?',
    'sh.textWin': 'Selei o Rift no RIFTFALL! {kills} abates e {score} pontos 🏆 Consegue me superar?',
    'sh.cta': 'Jogue grátis no navegador',
    'sh.copied': 'Texto copiado: cole no WhatsApp ou onde quiser',
    'sh.ship': 'NAVE'
  }
};

// Contenido del juego (armas, mejoras, naves, talentos, misiones). En español sale de content.js.
const CONTENT = {
  en: {
    tier: { Gratis: 'Free', Común: 'Common', Rara: 'Rare', Épica: 'Epic', Legendaria: 'Legendary' },
    ship: {
      spark: 'Standard interceptor. Balanced and reliable.',
      vanguard: 'Reinforced hull. Starts with Nova Pulse.',
      phantom: 'Fast and lethal. Starts with Orbital Blades.',
      tempest: 'Plasma core. Starts with Arc Coil.',
      leviathan: 'Legendary battleship (300 units). Homing missiles.'
    },
    weapon: {
      blaster: ['Pulse Blaster', ['Fires at the nearest enemy', '+1 projectile', '+40% damage', '+1 projectile and pierces 1', '+55% damage, -15% cooldown'], 'Pulse Storm', 'Continuous burst that pierces enemies'],
      orbit: ['Orbital Blades', ['Blades spin around you', '+1 blade', '+50% damage, larger radius', '+1 blade, faster', '+1 blade, +40% damage'], 'Singularity', 'Seven giant blades at full speed'],
      arc: ['Arc Coil', ['Lightning that jumps between enemies', '+2 jumps', '+60% damage', '-22% cooldown, +1 jump', '+45% damage, +3 jumps'], 'Tempest', 'Lightning that jumps between 14 enemies'],
      nova: ['Nova Pulse', ['Shockwave that pushes enemies back', '+25% radius', '+60% damage', '-20% cooldown, larger radius', '+50% damage, +20% radius'], 'Supernova', 'Huge, frequent explosion'],
      missile: ['Homing Missiles', ['Missiles that chase and explode', '+1 missile', '+45% damage', '+1 missile, bigger explosion', '+40% damage, -15% cooldown'], 'Swarm', 'Five missiles with massive explosions'],
      lance: ['Rift Lance', ['Piercing beam where you face', '+45% width', '+55% damage', '-20% cooldown, also fires backwards', '+45% damage, +25% range'], 'Void Ray', 'Colossal beam forwards and backwards']
    },
    passive: {
      might: ['Amplifier', '+12% damage'],
      haste: ['Overclock', '-7% cooldown'],
      thrust: ['Thrusters', '+8% speed'],
      magnet: ['Magnetar', '+35% pickup radius'],
      hull: ['Plating', '+20 max HP and +1 armor'],
      repair: ['Nanorepair', '+0.35 HP per second'],
      crit: ['Critical Matrix', '+6% crit chance'],
      area: ['Expander', '+10% area of effect'],
      multi: ['Multiplexer', '+1 projectile'],
      growth: ['Data Siphon', '+10% experience'],
      fortune: ['Shard Lens', '+10% Shard chance']
    },
    talent: {
      hull: ['Reinforced Hull', '+6% max HP'],
      power: ['Calibration', '+4% damage'],
      reflex: ['Reflexes', '-3% cooldown'],
      engines: ['Tuned Engines', '+3% speed'],
      magnet: ['Crystal Magnet', '+12% pickup radius'],
      memory: ['Combat Memory', '+4% experience']
    },
    mission: {
      runs3: 'Play 3 runs',
      kills500: 'Defeat 500 enemies',
      survive5: 'Survive 5:00 in one run',
      boss1: 'Defeat a Rift Guardian',
      combo200: 'Reach a 200 combo'
    }
  },
  pt: {
    tier: { Gratis: 'Grátis', Común: 'Comum', Rara: 'Rara', Épica: 'Épica', Legendaria: 'Lendária' },
    ship: {
      spark: 'Interceptador de série. Equilibrado e confiável.',
      vanguard: 'Casco reforçado. Começa com Nova Pulse.',
      phantom: 'Rápida e letal. Começa com Lâminas Orbitais.',
      tempest: 'Núcleo de plasma. Começa com Bobina de Arco.',
      leviathan: 'Encouraçado lendário (300 unidades). Mísseis teleguiados.'
    },
    weapon: {
      blaster: ['Pulse Blaster', ['Atira no inimigo mais próximo', '+1 projétil', '+40% dano', '+1 projétil e perfura 1', '+55% dano, -15% recarga'], 'Tempestade de Pulsos', 'Rajada contínua que atravessa inimigos'],
      orbit: ['Lâminas Orbitais', ['Lâminas giram ao seu redor', '+1 lâmina', '+50% dano, mais raio', '+1 lâmina, mais rápidas', '+1 lâmina, +40% dano'], 'Singularidade', 'Sete lâminas gigantes a toda velocidade'],
      arc: ['Bobina de Arco', ['Raio que salta entre inimigos', '+2 saltos', '+60% dano', '-22% recarga, +1 salto', '+45% dano, +3 saltos'], 'Tormenta', 'Raios que saltam entre 14 inimigos'],
      nova: ['Nova Pulse', ['Onda de choque que repele', '+25% raio', '+60% dano', '-20% recarga, mais raio', '+50% dano, +20% raio'], 'Supernova', 'Explosão gigante e frequente'],
      missile: ['Mísseis Teleguiados', ['Mísseis que perseguem e explodem', '+1 míssil', '+45% dano', '+1 míssil, explosão maior', '+40% dano, -15% recarga'], 'Enxame', 'Cinco mísseis de explosão enorme'],
      lance: ['Lança do Rift', ['Raio perfurante para onde você mira', '+45% largura', '+55% dano', '-20% recarga, também atira para trás', '+45% dano, +25% alcance'], 'Raio do Vazio', 'Raio colossal para frente e para trás']
    },
    passive: {
      might: ['Amplificador', '+12% dano'],
      haste: ['Overclock', '-7% recarga'],
      thrust: ['Propulsores', '+8% velocidade'],
      magnet: ['Magnetar', '+35% raio de coleta'],
      hull: ['Blindagem', '+20 vida máx. e +1 armadura'],
      repair: ['Nanorreparo', '+0,35 vida por segundo'],
      crit: ['Matriz Crítica', '+6% chance de crítico'],
      area: ['Expansor', '+10% área de efeito'],
      multi: ['Multiplexador', '+1 projétil'],
      growth: ['Sifão de Dados', '+10% experiência'],
      fortune: ['Lente de Shards', '+10% chance de Shards']
    },
    talent: {
      hull: ['Casco Reforçado', '+6% vida máxima'],
      power: ['Calibração', '+4% dano'],
      reflex: ['Reflexos', '-3% recarga'],
      engines: ['Motores Ajustados', '+3% velocidade'],
      magnet: ['Ímã de Cristais', '+12% raio de coleta'],
      memory: ['Memória de Combate', '+4% experiência']
    },
    mission: {
      runs3: 'Jogue 3 partidas',
      kills500: 'Derrote 500 inimigos',
      survive5: 'Sobreviva 5:00 em uma partida',
      boss1: 'Derrote um Guardião do Rift',
      combo200: 'Faça um combo de 200'
    }
  }
};

/** Texto traducido. Las variables van entre llaves: t('hud.level', { n: 3 }). */
export function t(key, vars) {
  let s = UI[lang]?.[key] ?? UI.es[key] ?? key;
  if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
  return s;
}

const C = CONTENT[lang];

export const tx = {
  shipDesc: (key) => C?.ship[key] ?? SHIPS[key]?.desc ?? '',
  shipTier: (key) => C?.tier[SHIPS[key]?.tier] ?? SHIPS[key]?.tier ?? '',
  weaponName: (id) => C?.weapon[id]?.[0] ?? WEAPONS[id].name,
  weaponDesc: (id, level) => C?.weapon[id]?.[1][level - 1] ?? WEAPONS[id].desc[level - 1],
  evoName: (id) => C?.weapon[id]?.[2] ?? WEAPONS[id].evo.name,
  evoDesc: (id) => C?.weapon[id]?.[3] ?? WEAPONS[id].evo.desc,
  passiveName: (id) => C?.passive[id]?.[0] ?? PASSIVES[id].name,
  passiveDesc: (id) => C?.passive[id]?.[1] ?? PASSIVES[id].desc,
  talentName: (id) => C?.talent[id]?.[0] ?? TALENTS[id].name,
  talentDesc: (id) => C?.talent[id]?.[1] ?? TALENTS[id].desc,
  missionName: (id, fallback = '') => C?.mission[id] ?? fallback,
  /** "Alas PHANTOM" */
  partName: (id) => {
    const p = partPerk(id);
    return p ? t('pt.name', { slot: t(`pt.slot.${p.slot}`), ship: SHIPS[p.design].name }) : id;
  },
  /** "+4% velocidad" para una pieza de nivel lv */
  partPerk: (id, lv) => {
    const p = partPerk(id);
    if (!p) return '';
    const v = p.per * lv;
    const pct = ['armor', 'regen'].includes(p.stat) ? +v.toFixed(1) : Math.round(v * 100);
    return t(`pt.stat.${p.stat}`, { v: pct });
  }
};

/** Traduce los textos fijos del HTML marcados con data-i18n, data-i18n-html, data-i18n-title, data-i18n-aria y data-i18n-placeholder. */
export function applyStatic(root = document) {
  for (const n of root.querySelectorAll('[data-i18n]')) n.textContent = t(n.dataset.i18n);
  for (const n of root.querySelectorAll('[data-i18n-html]')) n.innerHTML = t(n.dataset.i18nHtml);
  for (const n of root.querySelectorAll('[data-i18n-title]')) n.title = t(n.dataset.i18nTitle);
  for (const n of root.querySelectorAll('[data-i18n-aria]')) n.setAttribute('aria-label', t(n.dataset.i18nAria));
  for (const n of root.querySelectorAll('[data-i18n-placeholder]')) n.placeholder = t(n.dataset.i18nPlaceholder);
}
